<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { useCampaignStore } from '@/stores';
import { DtModal } from '@dialpad/dialtone/vue3/lib/modal';
import type { ModalProps } from '@/app/components/modal/modals';
import { DtButton } from '@dialpad/dialtone/vue3/lib/button';
import { closeModal, updateShow } from '@/app/components/modal/modals';
import { CampaignPatchActionEnum } from '@/apis/generated/dialbunny';
import { DATA_QA } from './archive-campaign.constants';
import { DATA_QA as MODAL_WRAPPER_DATA_QA } from '@/app/components/modal/modal-wrapper.constants';
import logger from '@/logger';
import { showAutoDismissToast, showErrorToast } from '@/noto';

const log = computed(() => logger.getChild(`ArchiveCampaign:${props.action}`));
log.value.debug('Mounting');
type ArchiveCampaignProps = ModalProps & {
  action: CampaignPatchActionEnum;
};

const props = defineProps<ArchiveCampaignProps>();

if (!props.meta?.id) {
  log.value.error('Missing campaign ID in modal meta');
  void closeModal();
}

const show = ref(false);

const actionMapToI18n: Map<CampaignPatchActionEnum, string> = new Map([
  [CampaignPatchActionEnum.Archive, 'ARCHIVE_CAMPAIGN'],
  [CampaignPatchActionEnum.Unarchive, 'UNARCHIVE_CAMPAIGN'],
]);

onMounted(() => {
  log.value.debug('Mounted');
  show.value = true;

  if (!actionMapToI18n.has(props.action)) {
    log.value.error(`Wrong action in modal props: ${props.action}`);
    void closeModal();
  }
});

const campaignStore = useCampaignStore();
const watchCampaign = campaignStore.watch(
  () => props.meta?.id ?? '',
  'ArchiveCampaign',
);

watch(
  [
    () => watchCampaign.value.item,
    () => watchCampaign.value.loading,
    () => watchCampaign.value.error,
  ],
  ([campaignToArchive, loading, error]) => {
    if (error) {
      log.value.warn(
        `Error fetching campaign. Status: ${error.response.status}, Message: ${error.message}`,
      );
      void closeModal();
      void showErrorToast({
        content: {
          body: {
            key: 'TOAST_CAMPAIGN_FETCH_ERROR_BODY',
          },
        },
      });
    } else if (!loading && !campaignToArchive) {
      log.value.log('Campaign is deleted');
      void closeModal();
      void showErrorToast({
        content: {
          body: {
            key: 'TOAST_CAMPAIGN_DELETED_BODY',
          },
        },
      });
    }
  },
);

watch(
  [() => watchCampaign.value.item, () => props.action],
  ([campaign, action]) => {
    if (action === CampaignPatchActionEnum.Archive && campaign?.dateArchived) {
      log.value.log('Campaign is already archived');
      void closeModal();
      void showErrorToast({
        content: {
          body: {
            key: `TOAST_${actionMapToI18n.get(props.action)}_ALREADY_BODY`,
          },
        },
      });
    } else if (
      action === CampaignPatchActionEnum.Unarchive &&
      !campaign?.dateArchived
    ) {
      log.value.log('Campaign is not archived');
      void closeModal();
      void showErrorToast({
        content: {
          body: {
            key: `TOAST_${actionMapToI18n.get(props.action)}_ALREADY_BODY`,
          },
        },
      });
    }
  },
);

const confirm = async () => {
  if (!watchCampaign.value.item?.id) {
    log.value.error('campaign ID is missing in watchCampaign');
    return;
  }
  log.value.debug('Archiving campaign');
  // closing modal right away for proper router navigation
  void closeModal();
  campaignStore.update(
    watchCampaign.value.item.id,
    {
      action: props.action,
    },
    {
      onSuccess: async () => {
        void showAutoDismissToast({
          content: {
            title: {
              key: `TOAST_${actionMapToI18n.get(props.action)}_TITLE`,
            },
          },
        });
        void campaignStore.refresh();
      },
      onRetry: () => {
        // show toast
      },
      onError: (error) => {
        log.value.log('Error archiving campaign', error);
        void showErrorToast({
          content: {
            body: {
              key: `TOAST_${actionMapToI18n.get(props.action)}_ERROR_BODY`,
            },
          },
        });
      },
    },
  );
};

const cancel = () => {
  void closeModal();
};
</script>
<template>
  <DtModal
    :data-qa="MODAL_WRAPPER_DATA_QA.DIALOG"
    :title="$t(`${actionMapToI18n.get(props.action)}_MODAL_TITLE`)"
    :close-button-props="{
      ariaLabel: $t('MODAL_CLOSE_ARIA_LABEL'),
      'data-qa': MODAL_WRAPPER_DATA_QA.CLOSE,
    }"
    :show="show"
    content-class="d-p4 d-mn4"
    @update:show="updateShow"
  >
    <div class="d-d-flex d-jc-flex-end d-mt16">
      <DtButton
        :data-qa="DATA_QA.CANCEL"
        importance="clear"
        kind="muted"
        @click="cancel"
      >
        {{ $t(`${actionMapToI18n.get(props.action)}_CANCEL`) }}
      </DtButton>
      <DtButton
        :data-qa="DATA_QA.CONFIRM"
        class="d-ml8"
        importance="primary"
        kind="danger"
        @click="confirm"
      >
        {{ $t(`${actionMapToI18n.get(props.action)}_CONFIRM`) }}
      </DtButton>
    </div>
  </DtModal>
</template>
